/**
 * How the transcript fold turns a `report_findings` call that recorded
 * findings into its card (`findings-card.tsx`), from which the person can have
 * the agent remove them, or dismiss them, in one click. Only a saved message carries the call's
 * input and the lines its accepted result saved: live events cut long strings,
 * so a running call shows nothing until its message is saved. A call with
 * nothing to act on (an empty list, or only `not_dead`) stays a step, as does
 * one with an unusable result or a refusal: the model reads the refusal and
 * calls again. A completed `not_dead` call in a visible saved message marks
 * what it names as kept on the cards of its pull request, which their button
 * leaves out. The card says "dismissed" for them: to the person, "kept" could
 * mean the pull request, the code or the cleanup.
 */
import type { SessionMessageAssistant } from '@opencode/client/promise';

import type { SessionPart } from './transcript-model';

import {
  REPORT_FINDINGS_TOOL,
  reportFindingsInputSchema,
  reportFindingsResultSchema,
} from '@/lib/code-sessions/session-tools/report-findings';
import { SUBMIT_BATCH_TOOL_NAME, SUBMIT_RESULT_TOOL_NAME } from '@/lib/code-sessions/tool-steps';

type ToolContent = Extract<SessionMessageAssistant['content'][number], { type: 'tool' }>;

export type FindingsPart = Extract<SessionPart, { type: 'findings' }>;

/**
 * Tools whose live calls make no trace step: a cubic run's result, which the
 * turn's result card shows, batch verdicts shown by the session findings rather than
 * meaningless trace steps, and a findings report, whose saved message shows it as a
 * card or, with nothing to act on, as a step.
 */
export const STEPLESS_LIVE_TOOLS: ReadonlySet<string> = new Set([
  SUBMIT_RESULT_TOOL_NAME,
  SUBMIT_BATCH_TOOL_NAME,
  REPORT_FINDINGS_TOOL,
]);

/** Whether `content` is a `report_findings` call cubic has not answered yet. */
export function isUncheckedFindingsReport(content: ToolContent): boolean {
  return (
    content.name === REPORT_FINDINGS_TOOL &&
    content.state.status !== 'completed' &&
    content.state.status !== 'error'
  );
}

/**
 * A saved, accepted `report_findings` call as its card, or undefined for any
 * other call, one with nothing to act on, or one without matching accepted
 * identities and lines in its saved result.
 */
export function savedFindingsPart(content: ToolContent): FindingsPart | undefined {
  if (content.name !== REPORT_FINDINGS_TOOL || content.state.status !== 'completed') {
    return undefined;
  }
  const parsed = reportFindingsInputSchema.safeParse(content.state.input);
  if (!parsed.success || parsed.data.commit === undefined || !parsed.data.findings?.length) {
    return undefined;
  }
  let result;
  try {
    const text = content.state.content
      .filter((part) => part.type === 'text')
      .map((part) => part.text)
      .join('\n');
    result = reportFindingsResultSchema.safeParse(JSON.parse(text));
  } catch {
    return undefined;
  }
  if (
    !result.success ||
    result.data.findings.length !== parsed.data.findings.length ||
    result.data.findings.some(
      (finding, index) =>
        finding.name !== parsed.data.findings![index]?.name ||
        finding.path !== parsed.data.findings![index]?.path,
    )
  ) {
    return undefined;
  }
  return {
    type: 'findings',
    callId: content.id,
    pullRequest: parsed.data.pull_request,
    commit: parsed.data.commit,
    ...(parsed.data.summary === undefined ? {} : { summary: parsed.data.summary }),
    findings: parsed.data.findings.map(
      ({ name, path, reason, evidence, label, change }, index) => ({
        name,
        path,
        line: result.data.findings[index]!.line,
        reason,
        evidence,
        ...(label === undefined ? {} : { label }),
        ...(change === undefined ? {} : { change }),
      }),
    ),
  };
}

/** What a completed `report_findings` call records as not dead code, by pull request. */
function keptVerdicts(
  content: ToolContent,
): Array<{ pullRequest: number; name: string; path?: string }> {
  if (content.name !== REPORT_FINDINGS_TOOL || content.state.status !== 'completed') {
    return [];
  }
  const parsed = reportFindingsInputSchema.safeParse(content.state.input);
  if (!parsed.success) {
    return [];
  }
  const pullRequest = parsed.data.pull_request;
  return (parsed.data.not_dead ?? []).map(({ name, path }) => ({ pullRequest, name, path }));
}

/**
 * Marks as kept, on every card of the same pull request, what a completed `report_findings` call
 * in the visible saved assistant `messages` records as not dead code: someone said to keep it.
 * A verdict without a path keeps every finding of that name. It runs once the transcript is
 * folded, so a visible verdict marks a card whichever came first.
 */
export function markKeptFindings(
  parts: readonly SessionPart[],
  messages: readonly SessionMessageAssistant[],
): void {
  const verdicts = messages
    .flatMap((message) => message.content)
    .flatMap((content) => (content.type === 'tool' ? keptVerdicts(content) : []));
  if (verdicts.length === 0) {
    return;
  }
  for (const part of parts) {
    if (part.type !== 'findings') {
      continue;
    }
    const keeps = (finding: FindingsPart['findings'][number]) =>
      verdicts.some(
        ({ pullRequest, name, path }) =>
          pullRequest === part.pullRequest &&
          name === finding.name &&
          (path === undefined || path === finding.path),
      );
    if (part.findings.some(keeps)) {
      part.findings = part.findings.map((finding) =>
        keeps(finding) ? { ...finding, kept: true } : finding,
      );
    }
  }
}

/** The card's findings still to act on: all but those someone kept. */
export function removableFindings(part: Pick<FindingsPart, 'findings'>): FindingsPart['findings'] {
  return part.findings.filter((finding) => finding.kept !== true);
}
